// Self-enrollment admin management — SPA client for
// /api/companies/:id/enrollment* (Access Provider Phase 3, Task 6 on the
// API side). Mirrors the shape/style of apps/admin/src/api/accessReaders.ts:
// DTOs come from @hub-wifi/shared so the SPA and API can't drift; every
// function takes the Auth0 access `token` explicitly, threaded last.
import type { CoworkerEnrollment } from '@hub-wifi/shared'
import { apiRequest } from './client'

export type { CoworkerEnrollment }

/** GET /api/companies/:id/enrollment response — invite token + approval flag. */
export interface CompanyEnrollmentConfig {
  enrollmentToken: string | null
  enrollmentApprovalRequired: boolean
  inviteUrl: string | null
}

/** POST /api/companies/:id/enrollment/token response — freshly rotated token. */
export interface RegenerateEnrollmentTokenResult {
  enrollmentToken: string
  inviteUrl: string
}

export function getCompanyEnrollment(
  companyId: string,
  token: string,
): Promise<CompanyEnrollmentConfig> {
  return apiRequest<CompanyEnrollmentConfig>(`/api/companies/${companyId}/enrollment`, { token })
}

export function regenerateEnrollmentToken(
  companyId: string,
  token: string,
): Promise<RegenerateEnrollmentTokenResult> {
  return apiRequest<RegenerateEnrollmentTokenResult>(
    `/api/companies/${companyId}/enrollment/token`,
    { method: 'POST', token },
  )
}

export function setEnrollmentApproval(
  companyId: string,
  required: boolean,
  token: string,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/api/companies/${companyId}/enrollment/approval`, {
    method: 'PUT',
    body: { required },
    token,
  })
}

export async function listPendingEnrollments(
  companyId: string,
  token: string,
): Promise<CoworkerEnrollment[]> {
  const { pending } = await apiRequest<{ pending: CoworkerEnrollment[] }>(
    `/api/companies/${companyId}/enrollment/pending`,
    { token },
  )
  return pending
}

export function approveEnrollment(
  companyId: string,
  enrollmentId: string,
  token: string,
): Promise<{ ok: boolean; coworkerId: string }> {
  return apiRequest<{ ok: boolean; coworkerId: string }>(
    `/api/companies/${companyId}/enrollment/pending/${enrollmentId}/approve`,
    { method: 'POST', token },
  )
}

export function rejectEnrollment(
  companyId: string,
  enrollmentId: string,
  token: string,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/api/companies/${companyId}/enrollment/pending/${enrollmentId}/reject`,
    { method: 'POST', token },
  )
}
